/* eslint-disable react/require-default-props */
import React from 'react';
import { Box, InputGroup, InputLeftElement, Input, InputRightElement, Text, InputProps, InputGroupProps } from '@chakra-ui/core';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

interface SearchBarProps extends InputProps {
  onSearch: (value: string) => void;
  loading: boolean;
  inputGroupProps?: InputGroupProps;
  bottom?: string;
}

function SearchBarComponent({
  onSearch,
  loading,
  inputGroupProps,
  bottom = '1px',
  value: initialValue,
  ...inputProps
}: SearchBarProps) {
  const { t } = useTranslation('watchedAndFollowedPositions');

  const [searchTerm, setSearchTerm] = React.useState(initialValue);
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    setSearchTerm(initialValue);
  }, [initialValue]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch(event.currentTarget.value);
    }
    if (event.key === 'Escape') {
      setSearchTerm('');
      onSearch('');
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    setIsFocused(false);
    onSearch(value);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  return (
    <Box w="auto" data-testid="watched-and-followed-positions-search-bar-container">
      <InputGroup position="relative" background="white" borderRadius="md" {...inputGroupProps}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.300" />
        </InputLeftElement>
        <Input
          data-testid="watched-and-followed-positions-search-bar-input"
          aria-label="watched-and-followed-positions-search-bar-input"
          name="watched-and-followed-positions-search-bar-input"
          type="text"
          value={searchTerm}
          placeholder={t('searchBox.placeholder')}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          onFocus={handleFocus}
          onBlur={handleBlur}
          isDisabled={loading}
          {...inputProps}
        />
        <InputRightElement width="auto" px={2}>
          {!loading && isFocused && (
            <Box position="absolute" bottom={bottom} right="9px" width="max-content">
              <Text fontSize="11px" color="gray.400">
                {t('searchBox.enterToSearch')}
              </Text>
            </Box>
          )}
        </InputRightElement>
      </InputGroup>
    </Box>
  );
}

export const SearchBar = React.memo(SearchBarComponent);
